import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ChartLine, Scales, TrendUp, Calculator, Warning, Target, ArrowRight, CheckCircle, XCircle, Lightning, ChartBar } from "phosphor-react";
import { updateSeoMetadata, getArticleAlternates } from "../../lib/seo";

const ProfitFactorTradingPT = () => {
  useEffect(() => {
    updateSeoMetadata({
      title: 'Profit Factor no Trading | Como Funciona e Como Melhorar',
      description: 'Entenda o que é profit factor no trading, como calcular e como usar essa métrica para melhorar seus resultados.',
      path: '/pt/profit-factor-trading',
      lang: 'pt-BR',
      alternates: getArticleAlternates('/pt/profit-factor-trading'),
    });
    
    return () => {
      document.title = 'GENI — Turn trading data into better decisions';
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      {/* Header */}
      <header className="border-b border-gray-800/50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/geni-logo.png" alt="GENI" className="h-8" />
            <span className="text-xl font-bold">GENI</span>
          </Link>
          <Link
            to="/signup"
            className="bg-[#00FF85] text-black px-5 py-2 rounded-lg font-semibold hover:bg-[#00DD70] transition-colors"
          >
            Começar Grátis
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-16">
        {/* Breadcrumb */}
        <nav className="text-sm text-gray-500 mb-8">
          <Link to="/" className="hover:text-[#00FF85]">Home</Link>
          <span className="mx-2">/</span>
          <span className="text-gray-400">Métricas de Trading</span>
        </nav>

        {/* Category Tag */}
        <div className="flex items-center gap-2 text-blue-400 text-sm font-medium mb-4">
          <Calculator size={16} weight="bold" />
          <span>Métricas de Trading</span>
        </div>

        {/* H1 */}
        <h1 className="text-4xl sm:text-5xl font-bold mb-4 leading-tight">
          O Que é Profit Factor no Trading
        </h1>

        <p className="text-xl text-gray-400 mb-12">
          (E Por Que Ele Importa)
        </p>

        {/* Introdução */}
        <div className="prose prose-invert max-w-none">
          <p className="text-lg text-gray-300 leading-relaxed mb-6">
            Muitos traders olham apenas para o <strong className="text-white">win rate</strong> ou para o <strong className="text-white">lucro total</strong>. Mas essas métricas sozinhas podem enganar. Um trader pode ter 70% de win rate e ainda perder dinheiro. Outro pode ter 40% de acerto e ser extremamente lucrativo.
          </p>

          <p className="text-lg text-gray-300 leading-relaxed mb-12">
            O <strong className="text-white">profit factor</strong> é uma métrica que corta através da superfície e mostra a qualidade real da sua estratégia. É uma das ferramentas mais importantes para entender se o que você está fazendo realmente funciona.
          </p>

          {/* Seção 1 */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <Calculator size={24} className="text-blue-400" />
              </div>
              <h2 className="text-2xl font-bold">O Que é Profit Factor</h2>
            </div>

            <p className="text-gray-300 leading-relaxed mb-6">
              Profit Factor é a razão entre o lucro total e o prejuízo total das suas operações. Em outras palavras: para cada R$ 1 que você perde, quanto você ganha?
            </p>

            <div className="bg-blue-500/5 border border-blue-500/20 rounded-xl p-8 mb-6">
              <h3 className="font-semibold text-blue-400 mb-4 text-center">Fórmula do Profit Factor</h3>
              <div className="bg-gray-900/50 rounded-lg p-6 text-center">
                <p className="text-2xl font-mono text-white">
                  PF = <span className="text-green-400">Lucro Total</span> / <span className="text-red-400">Prejuízo Total</span>
                </p>
              </div>
            </div>

            <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6 mb-6">
              <h3 className="font-semibold text-white mb-4">Exemplo Prático:</h3>
              <div className="grid sm:grid-cols-3 gap-4 text-center mb-4">
                <div className="bg-green-500/10 rounded-lg p-4">
                  <p className="text-sm text-gray-500 mb-1">Lucro total (gains)</p>
                  <p className="text-2xl font-bold text-green-400">R$ 5.000</p>
                </div>
                <div className="bg-red-500/10 rounded-lg p-4">
                  <p className="text-sm text-gray-500 mb-1">Prejuízo total (losses)</p>
                  <p className="text-2xl font-bold text-red-400">R$ 2.500</p>
                </div>
                <div className="bg-blue-500/10 rounded-lg p-4">
                  <p className="text-sm text-gray-500 mb-1">Profit Factor</p>
                  <p className="text-2xl font-bold text-blue-400">2.0</p>
                </div>
              </div>
              <p className="text-gray-400 text-sm text-center">
                PF = 5.000 / 2.500 = <strong className="text-white">2.0</strong> → Para cada R$ 1 perdido, você ganhou R$ 2.
              </p>
            </div>
          </section>

          {/* Seção 2 */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-[#00FF85]/10 flex items-center justify-center">
                <Target size={24} className="text-[#00FF85]" />
              </div>
              <h2 className="text-2xl font-bold">Por Que o Profit Factor é Importante</h2>
            </div>

            <p className="text-gray-300 leading-relaxed mb-6">
              O profit factor não é apenas mais um número. Ele revela informações fundamentais sobre sua operação.
            </p>

            <div className="grid sm:grid-cols-3 gap-4 mb-6">
              <div className="bg-[#00FF85]/5 border border-[#00FF85]/20 rounded-xl p-5">
                <h3 className="font-semibold text-[#00FF85] mb-2">Qualidade da Estratégia</h3>
                <p className="text-gray-400 text-sm">Mostra se sua estratégia gera mais do que perde. Um PF acima de 1 significa que você está ganhando dinheiro no agregado.</p>
              </div>

              <div className="bg-[#00FF85]/5 border border-[#00FF85]/20 rounded-xl p-5">
                <h3 className="font-semibold text-[#00FF85] mb-2">Eficiência</h3>
                <p className="text-gray-400 text-sm">Quanto maior o PF, mais eficiente você é em transformar risco em retorno. Menos "desperdício" de capital.</p>
              </div>

              <div className="bg-[#00FF85]/5 border border-[#00FF85]/20 rounded-xl p-5">
                <h3 className="font-semibold text-[#00FF85] mb-2">Consistência</h3>
                <p className="text-gray-400 text-sm">Um PF estável ao longo do tempo indica uma estratégia consistente e sustentável.</p>
              </div>
            </div>

            <p className="text-gray-300 leading-relaxed">
              Diferente do lucro total (que pode ser fruto de sorte) ou do win rate (que pode esconder perdas grandes), o profit factor mostra a <strong className="text-white">relação real entre ganhos e perdas</strong>.
            </p>
          </section>

          {/* Seção 3 */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                <ChartBar size={24} className="text-amber-400" />
              </div>
              <h2 className="text-2xl font-bold">Qual é um Bom Profit Factor</h2>
            </div>

            <p className="text-gray-300 leading-relaxed mb-6">
              Nem todo profit factor é igual. Aqui está um guia de referência:
            </p>

            <div className="space-y-3 mb-6">
              <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 flex items-center gap-4">
                <div className="text-2xl font-bold text-red-400 w-20">&lt; 1.0</div>
                <div>
                  <p className="font-semibold text-red-400">Prejuízo</p>
                  <p className="text-gray-400 text-sm">Você está perdendo mais do que ganha. Estratégia precisa de revisão urgente.</p>
                </div>
              </div>

              <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 flex items-center gap-4">
                <div className="text-2xl font-bold text-amber-400 w-20">1.0–1.5</div>
                <div>
                  <p className="font-semibold text-amber-400">Fraco / Marginal</p>
                  <p className="text-gray-400 text-sm">Lucrativo, mas com pouca margem de segurança. Qualquer variação pode virar prejuízo.</p>
                </div>
              </div>

              <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 flex items-center gap-4">
                <div className="text-2xl font-bold text-green-400 w-20">1.5–2.0</div>
                <div>
                  <p className="font-semibold text-green-400">Bom</p>
                  <p className="text-gray-400 text-sm">Estratégia sólida com margem confortável. A maioria dos traders profissionais opera nessa faixa.</p>
                </div>
              </div>

              <div className="bg-[#00FF85]/10 border border-[#00FF85]/30 rounded-xl p-4 flex items-center gap-4">
                <div className="text-2xl font-bold text-[#00FF85] w-20">2.0+</div>
                <div>
                  <p className="font-semibold text-[#00FF85]">Forte / Excelente</p>
                  <p className="text-gray-400 text-sm">Performance excepcional. Verifique se a amostra é grande o suficiente para ser confiável.</p>
                </div>
              </div>
            </div>

            <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-5">
              <div className="flex items-start gap-3">
                <Warning size={20} className="text-amber-400 mt-1 flex-shrink-0" />
                <p className="text-gray-300 text-sm">
                  <strong className="text-amber-400">Cuidado com amostras pequenas:</strong> Um PF de 3.0 com apenas 10 trades não significa nada. Você precisa de pelo menos 30-50 trades (idealmente 100+) para confiar no número.
                </p>
              </div>
            </div>
          </section>

          {/* Seção 4 */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                <Scales size={24} className="text-purple-400" />
              </div>
              <h2 className="text-2xl font-bold">Profit Factor vs Win Rate</h2>
            </div>

            <p className="text-gray-300 leading-relaxed mb-6">
              Muitos traders se obcecam com win rate (percentual de trades vencedores). Mas win rate sozinho pode ser completamente enganoso.
            </p>

            <div className="grid sm:grid-cols-2 gap-6 mb-6">
              <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-5">
                <h3 className="font-semibold text-red-400 mb-3">Alto Win Rate, Baixo PF</h3>
                <ul className="text-gray-400 text-sm space-y-2">
                  <li>• Win rate: 80%</li>
                  <li>• Ganho médio: R$ 50</li>
                  <li>• Perda média: R$ 300</li>
                  <li>• Em 10 trades: 8 gains (R$ 400), 2 losses (R$ 600)</li>
                  <li>• <strong className="text-red-400">Resultado: -R$ 200</strong></li>
                </ul>
              </div>

              <div className="bg-[#00FF85]/5 border border-[#00FF85]/20 rounded-xl p-5">
                <h3 className="font-semibold text-[#00FF85] mb-3">Baixo Win Rate, Alto PF</h3>
                <ul className="text-gray-400 text-sm space-y-2">
                  <li>• Win rate: 40%</li>
                  <li>• Ganho médio: R$ 300</li>
                  <li>• Perda média: R$ 80</li>
                  <li>• Em 10 trades: 4 gains (R$ 1.200), 6 losses (R$ 480)</li>
                  <li>• <strong className="text-[#00FF85]">Resultado: +R$ 720</strong></li>
                </ul>
              </div>
            </div>

            <p className="text-gray-300 leading-relaxed">
              O profit factor mostra a qualidade real porque considera <strong className="text-white">quanto você ganha quando acerta</strong> versus <strong className="text-white">quanto você perde quando erra</strong>, não apenas a frequência de acertos.
            </p>
          </section>

          {/* Seção 5 */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <TrendUp size={24} className="text-blue-400" />
              </div>
              <h2 className="text-2xl font-bold">Profit Factor vs Risk Reward</h2>
            </div>

            <p className="text-gray-300 leading-relaxed mb-6">
              Risk Reward (RR) é a relação entre risco e retorno de um trade individual. Profit Factor é a mesma ideia, mas aplicada a todos os seus trades agregados.
            </p>

            <div className="bg-blue-500/5 border border-blue-500/20 rounded-xl p-6 mb-6">
              <h3 className="font-semibold text-blue-400 mb-4">A Relação Entre os Dois:</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <CheckCircle size={20} className="text-blue-400 mt-1 flex-shrink-0" />
                  <span className="text-gray-300"><strong className="text-white">RR individual alto + win rate razoável = PF alto</strong></span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle size={20} className="text-blue-400 mt-1 flex-shrink-0" />
                  <span className="text-gray-300">O PF é o resultado prático do seu RR médio combinado com seu win rate</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle size={20} className="text-blue-400 mt-1 flex-shrink-0" />
                  <span className="text-gray-300">Você pode planejar um RR de 1:3, mas se sempre fechar cedo, seu PF real será menor</span>
                </li>
              </ul>
            </div>

            <p className="text-gray-300 leading-relaxed">
              O profit factor mostra o RR que você <strong className="text-white">realmente praticou</strong>, não o que você planejou. É a verdade sobre sua execução.
            </p>
          </section>

          {/* Seção 6 */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                <ChartLine size={24} className="text-amber-400" />
              </div>
              <h2 className="text-2xl font-bold">A Importância da Amostra</h2>
            </div>

            <p className="text-gray-300 leading-relaxed mb-6">
              Um dos erros mais comuns é confiar em um profit factor calculado com poucos trades.
            </p>

            <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6 mb-6">
              <h3 className="font-semibold text-white mb-4">Quanto maior a amostra, mais confiável o PF:</h3>
              <div className="grid sm:grid-cols-3 gap-4 text-center">
                <div className="p-4 rounded-lg bg-red-500/10">
                  <p className="text-3xl font-bold text-red-400 mb-1">10</p>
                  <p className="text-xs text-gray-500">trades</p>
                  <p className="text-sm text-red-400 mt-2">Ruído estatístico</p>
                </div>
                <div className="p-4 rounded-lg bg-amber-500/10">
                  <p className="text-3xl font-bold text-amber-400 mb-1">30-50</p>
                  <p className="text-xs text-gray-500">trades</p>
                  <p className="text-sm text-amber-400 mt-2">Tendência visível</p>
                </div>
                <div className="p-4 rounded-lg bg-[#00FF85]/10">
                  <p className="text-3xl font-bold text-[#00FF85] mb-1">100+</p>
                  <p className="text-xs text-gray-500">trades</p>
                  <p className="text-sm text-[#00FF85] mt-2">Estatisticamente confiável</p>
                </div>
              </div>
            </div>

            <p className="text-gray-300 leading-relaxed">
              Poucos trades = resultado enganoso. A variância natural do mercado pode criar ilusões de performance. Consistência exige volume de dados.
            </p>
          </section>

          {/* Seção 7 */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                <Warning size={24} className="text-red-400" />
              </div>
              <h2 className="text-2xl font-bold">Erros Comuns ao Usar Profit Factor</h2>
            </div>

            <div className="space-y-4 mb-6">
              <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-2">
                  <XCircle size={20} className="text-red-400" />
                  <h3 className="font-semibold text-white">Confiar em Poucos Dados</h3>
                </div>
                <p className="text-gray-400 text-sm">Um PF de 4.0 com 5 trades não significa nada. Pode ser pura sorte. Espere ter pelo menos 30+ trades antes de tirar conclusões.</p>
              </div>

              <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-2">
                  <XCircle size={20} className="text-red-400" />
                  <h3 className="font-semibold text-white">Misturar Ativos Diferentes</h3>
                </div>
                <p className="text-gray-400 text-sm">Calcular um PF único para BTC, ações e forex juntos esconde informações importantes. Separe por ativo para entender onde você performa melhor.</p>
              </div>

              <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-2">
                  <XCircle size={20} className="text-red-400" />
                  <h3 className="font-semibold text-white">Não Separar Setups</h3>
                </div>
                <p className="text-gray-400 text-sm">Seu setup de rompimento pode ter PF 2.5 enquanto seu setup de reversão tem PF 0.8. Se você mistura, fica com um PF médio de 1.4 e não entende o problema.</p>
              </div>
            </div>
          </section>

          {/* Seção 8 */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                <Lightning size={24} className="text-purple-400" />
              </div>
              <h2 className="text-2xl font-bold">Por Que o PF Muda Entre Ativos</h2>
            </div>

            <p className="text-gray-300 leading-relaxed mb-6">
              É normal ter profit factors diferentes para diferentes ativos. Isso acontece por vários motivos:
            </p>

            <div className="grid sm:grid-cols-3 gap-4 mb-6">
              <div className="bg-purple-500/5 border border-purple-500/20 rounded-xl p-5">
                <h3 className="font-semibold text-purple-400 mb-2">Liquidez</h3>
                <p className="text-gray-400 text-sm">Ativos menos líquidos têm mais slippage, o que pode reduzir seu PF mesmo com a mesma estratégia.</p>
              </div>

              <div className="bg-purple-500/5 border border-purple-500/20 rounded-xl p-5">
                <h3 className="font-semibold text-purple-400 mb-2">Volatilidade</h3>
                <p className="text-gray-400 text-sm">Ativos muito voláteis podem gerar ganhos maiores, mas também stops mais frequentes. O balanço afeta o PF.</p>
              </div>

              <div className="bg-purple-500/5 border border-purple-500/20 rounded-xl p-5">
                <h3 className="font-semibold text-purple-400 mb-2">Comportamento</h3>
                <p className="text-gray-400 text-sm">Cada mercado tem personalidade. Seu setup pode funcionar bem em ações e mal em cripto.</p>
              </div>
            </div>

            <p className="text-gray-300 leading-relaxed">
              Por isso é importante calcular o PF separadamente para cada ativo ou categoria de ativos que você opera.
            </p>
          </section>

          {/* Seção 9 */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-[#00FF85]/10 flex items-center justify-center">
                <TrendUp size={24} className="text-[#00FF85]" />
              </div>
              <h2 className="text-2xl font-bold">Como Melhorar o Profit Factor</h2>
            </div>

            <p className="text-gray-300 leading-relaxed mb-6">
              Melhorar o PF exige trabalhar nas duas pontas: aumentar ganhos e reduzir perdas.
            </p>

            <div className="space-y-4 mb-6">
              <div className="bg-[#00FF85]/5 border border-[#00FF85]/20 rounded-xl p-5">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-[#00FF85] text-black font-bold flex items-center justify-center flex-shrink-0">1</div>
                  <div>
                    <h3 className="font-semibold text-white mb-2">Melhorar Entradas</h3>
                    <p className="text-gray-400 text-sm">Entradas mais precisas significam stops menores. Stop menor = perda menor quando errar, PF maior.</p>
                  </div>
                </div>
              </div>

              <div className="bg-[#00FF85]/5 border border-[#00FF85]/20 rounded-xl p-5">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-[#00FF85] text-black font-bold flex items-center justify-center flex-shrink-0">2</div>
                  <div>
                    <h3 className="font-semibold text-white mb-2">Cortar Prejuízo Rápido</h3>
                    <p className="text-gray-400 text-sm">Não deixe losses pequenos virarem grandes. Respeite o stop. Cada perda grande derruba seu PF significativamente.</p>
                  </div>
                </div>
              </div>

              <div className="bg-[#00FF85]/5 border border-[#00FF85]/20 rounded-xl p-5">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-[#00FF85] text-black font-bold flex items-center justify-center flex-shrink-0">3</div>
                  <div>
                    <h3 className="font-semibold text-white mb-2">Deixar Lucro Correr</h3>
                    <p className="text-gray-400 text-sm">Não feche trades vencedores cedo demais por medo. O lucro que você deixa na mesa reduz seu PF.</p>
                  </div>
                </div>
              </div>

              <div className="bg-[#00FF85]/5 border border-[#00FF85]/20 rounded-xl p-5">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-[#00FF85] text-black font-bold flex items-center justify-center flex-shrink-0">4</div>
                  <div>
                    <h3 className="font-semibold text-white mb-2">Refinar Setups</h3>
                    <p className="text-gray-400 text-sm">Abandone setups com PF baixo. Foque nos que funcionam. Use dados para tomar essa decisão, não intuição.</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Seção 10 */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <Target size={24} className="text-blue-400" />
              </div>
              <h2 className="text-2xl font-bold">Uso Prático do Profit Factor</h2>
            </div>

            <p className="text-gray-300 leading-relaxed mb-6">
              O PF não é só um número para olhar — é uma ferramenta de decisão.
            </p>

            <div className="bg-blue-500/5 border border-blue-500/20 rounded-xl p-6 mb-6">
              <h3 className="font-semibold text-blue-400 mb-4">Como usar o PF na prática:</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <CheckCircle size={20} className="text-blue-400 mt-1 flex-shrink-0" />
                  <span className="text-gray-300"><strong className="text-white">Comparar setups:</strong> Qual setup tem melhor PF? Foque nele.</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle size={20} className="text-blue-400 mt-1 flex-shrink-0" />
                  <span className="text-gray-300"><strong className="text-white">Melhorar decisões:</strong> Um setup com PF abaixo de 1 deve ser revisado ou abandonado.</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle size={20} className="text-blue-400 mt-1 flex-shrink-0" />
                  <span className="text-gray-300"><strong className="text-white">Identificar padrões:</strong> Seu PF é pior em certos horários? Certos ativos? Descubra onde você sangra.</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle size={20} className="text-blue-400 mt-1 flex-shrink-0" />
                  <span className="text-gray-300"><strong className="text-white">Acompanhar evolução:</strong> Seu PF está melhorando ao longo do tempo? Esse é o indicador real de progresso.</span>
                </li>
              </ul>
            </div>
          </section>

          {/* GENI Mention */}
          <section className="mb-16">
            <div className="bg-gradient-to-r from-[#00FF85]/10 via-[#00FF85]/5 to-transparent border border-[#00FF85]/20 rounded-2xl p-8">
              <h3 className="text-xl font-bold text-white mb-4">Calcule Seu Profit Factor Automaticamente</h3>
              <p className="text-gray-300 leading-relaxed mb-4">
                Ferramentas como o <strong className="text-[#00FF85]">GENI</strong> ajudam a calcular profit factor automaticamente e comparar performance entre setups e ativos. Sem planilhas, sem cálculos manuais — apenas dados claros para decisões melhores.
              </p>
              <Link
                to="/signup"
                className="inline-flex items-center gap-2 text-[#00FF85] font-semibold hover:underline"
              >
                Experimentar GENI Grátis <ArrowRight size={18} />
              </Link>
            </div>
          </section>

          {/* CTA Final */}
          <section className="mb-16">
            <div className="bg-gray-900/50 border border-gray-800 rounded-2xl p-8 text-center">
              <h2 className="text-2xl font-bold mb-4">Conheça Seu Profit Factor</h2>
              <p className="text-gray-400 mb-6 max-w-2xl mx-auto">
                Entender seu profit factor é essencial para melhorar sua consistência no trading. É a métrica que mostra a verdade sobre sua estratégia — sem ilusões, sem enganos.
              </p>
              <Link
                to="/signup"
                className="inline-flex items-center gap-2 bg-[#00FF85] text-black px-8 py-3 rounded-lg font-semibold hover:bg-[#00DD70] transition-colors"
              >
                Começar Grátis <ArrowRight size={20} />
              </Link>
            </div>
          </section>

          {/* FAQ */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-8">Perguntas Frequentes</h2>

            <div className="space-y-4">
              <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6">
                <h3 className="font-semibold text-white mb-2">O que é um bom profit factor?</h3>
                <p className="text-gray-400 text-sm">Acima de 1.5 é considerado bom, acima de 2.0 é excelente. Mas contexto importa: verifique se a amostra de trades é grande o suficiente (pelo menos 30-50 trades) antes de confiar no número.</p>
              </div>

              <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6">
                <h3 className="font-semibold text-white mb-2">Profit factor é melhor que win rate?</h3>
                <p className="text-gray-400 text-sm">Não é melhor ou pior — são métricas diferentes. Mas o PF mostra a qualidade real da estratégia porque considera o tamanho dos ganhos e perdas, não apenas a frequência. Use os dois juntos.</p>
              </div>

              <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6">
                <h3 className="font-semibold text-white mb-2">Dá pra confiar em PF com poucos trades?</h3>
                <p className="text-gray-400 text-sm">Não. Com poucos trades, o PF é dominado por variância aleatória. Você precisa de pelo menos 30-50 trades para ter uma noção, e 100+ para confiança real.</p>
              </div>

              <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6">
                <h3 className="font-semibold text-white mb-2">Como melhorar meu profit factor?</h3>
                <p className="text-gray-400 text-sm">Trabalhe nas duas pontas: reduza perdas (entradas mais precisas, stops respeitados) e aumente ganhos (deixe trades atingirem alvos). Analise seus dados para identificar onde você está sangrando dinheiro.</p>
              </div>
            </div>
          </section>

          {/* Related Articles */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Artigos Relacionados</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Link
                to="/pt/como-analisar-trades"
                className="bg-gray-900/50 border border-gray-800 rounded-xl p-5 hover:border-[#00FF85]/50 transition-colors group"
              >
                <h3 className="font-semibold text-white mb-2 group-hover:text-[#00FF85] transition-colors">Como Analisar Seus Trades</h3>
                <p className="text-gray-500 text-sm">Guia prático para analisar trades de forma estruturada.</p>
              </Link>

              <Link
                to="/pt/como-melhorar-risk-reward"
                className="bg-gray-900/50 border border-gray-800 rounded-xl p-5 hover:border-[#00FF85]/50 transition-colors group"
              >
                <h3 className="font-semibold text-white mb-2 group-hover:text-[#00FF85] transition-colors">Como Melhorar Risk Reward</h3>
                <p className="text-gray-500 text-sm">Técnicas para melhorar sua relação risco/retorno.</p>
              </Link>

              <Link
                to="/pt/por-que-traders-nao-evoluem"
                className="bg-gray-900/50 border border-gray-800 rounded-xl p-5 hover:border-[#00FF85]/50 transition-colors group"
              >
                <h3 className="font-semibold text-white mb-2 group-hover:text-[#00FF85] transition-colors">Por Que Traders Não Evoluem</h3>
                <p className="text-gray-500 text-sm">Entenda os erros comportamentais que impedem evolução.</p>
              </Link>

              <Link
                to="/pt/diario-de-trades-automatico"
                className="bg-gray-900/50 border border-gray-800 rounded-xl p-5 hover:border-[#00FF85]/50 transition-colors group"
              >
                <h3 className="font-semibold text-white mb-2 group-hover:text-[#00FF85] transition-colors">Diário de Trades Automático</h3>
                <p className="text-gray-500 text-sm">Como automatizar o registro dos seus trades.</p>
              </Link>
            </div>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-800/50 py-12">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <Link to="/" className="flex items-center gap-2">
              <img src="/geni-logo.png" alt="GENI" className="h-6" />
              <span className="font-semibold">GENI</span>
            </Link>
            <div className="flex items-center gap-6 text-sm text-gray-500">
              <Link to="/about" className="hover:text-white transition-colors">Sobre</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Termos</Link>
              <Link to="/privacy" className="hover:text-white transition-colors">Privacidade</Link>
            </div>
            <p className="text-sm text-gray-600">
              © 2025 GENI. Todos os direitos reservados.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ProfitFactorTradingPT;
